// src/layout/MainLayout/MainLayout.tsx
import { useEffect, useState } from 'react';
import { Grid, Layout } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast as message } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@store/helpers';
import {
  NotificationItem,
  useAcceptNotificationMutation,
  useGetNotificationsQuery,
} from '@store/api/notifications/notificationsApi';
import { AppConfirmationModalBase } from '@components/ui/AppConfirmationModalBase/AppConfirmationModalBase';
import { setCredentials } from '@store/slices/auth/authSlice';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { getDisplayUserLabel } from '@utils/userDisplay';
import { getMutationErrorMessage } from '@utils/mutationErrorMessage';
import { useInactiveProResumeNudge } from '@/hooks/useInactiveProResumeNudge';
import { AppInactiveProResumeNudgeModal } from '@components/ui/AppInactiveProResumeNudgeModal/AppInactiveProResumeNudgeModal';
import { AppInactiveProHeaderBanner } from '@components/ui/AppInactiveProHeaderBanner/AppInactiveProHeaderBanner';
import { trackInvitationResponded } from '@/lib/firebase';

import Footer from '@layout/Footer/Footer';
import Sidebar from '@layout/Sidebar/Sidebar';
import Header from '@layout/Header/Header';
import styles from './MainLayout.module.css';

const { Content } = Layout;
const { useBreakpoint } = Grid;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<string[]>([]);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const authToken = useAppSelector(state => state.auth.token);
  useNotificationSocket();
  const { data: notifications } = useGetNotificationsQuery(undefined, {
    skip: !authToken,
    refetchOnMountOrArgChange: true,
  });
  const [acceptNotification, { isLoading: isAcceptingNotification }] = useAcceptNotificationMutation();

  const activeNotification =
    location.pathname === '/user/notifications'
      ? null
      : (notifications?.items || []).find(
          item =>
            !dismissedNotificationIds.includes(
              item.notificationType === 'invite' ? item.inviteId : item.transferId
            )
        ) || null;

  const {
    open: inactiveProNudgeOpen,
    dismiss: dismissInactiveProNudge,
    resumePro: resumeInactivePro,
    isCheckoutLoading: isInactiveProCheckoutLoading,
    organizationLabel: inactiveProOrganizationLabel,
    showHeaderBanner: inactiveProShowHeaderBanner,
    billingHref: inactiveProBillingHref,
    variant: inactiveProNudgeVariant,
    grantEndsAt: inactiveProGrantEndsAt,
    inactivePaidPlanName,
  } = useInactiveProResumeNudge(Boolean(activeNotification));

  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileMenuOpen(prev => !prev);
      return;
    }
    setCollapsed(prev => !prev);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const buildNotificationDescriptionLines = (notification: NotificationItem) => {
    if (notification.notificationType === 'invite') {
      const lines = [
        t('invites.confirm.signedInAs', {
          email: notification.email || t('common.notAvailable'),
        }),
        t('invites.org.organizationLabel', {
          name: notification.organization?.name || t('common.notAvailable'),
        }),
        t(
          notification.inviteType === 'project'
            ? 'invites.project.roleLabel'
            : 'invites.org.roleLabel',
          {
            role: t(`roles.${notification.role}`),
          }
        ),
      ];

      if (notification.inviteType === 'project') {
        lines.splice(
          1,
          0,
          t('invites.project.projectLabel', {
            name: notification.project?.name || t('common.notAvailable'),
          })
        );
      } else if (notification.projectAccesses?.length) {
        lines.push(
          t('invites.org.projectsLabel', {
            projects: notification.projectAccesses
              .map(access => access.projectName || access.projectId)
              .filter(Boolean)
              .join(', '),
          })
        );
      }

      return lines;
    }

    if (notification.notificationType === 'project_transfer') {
      return [
        t('notifications.transferPopup.project', {
          project: notification.project?.name || t('common.notAvailable'),
        }),
        t('notifications.transferPopup.sourceOrganization', {
          organization: notification.fromOrganization?.name || t('common.notAvailable'),
        }),
        t('notifications.transferPopup.targetOrganization', {
          organization: notification.toOrganization?.name || t('common.notAvailable'),
        }),
        t('notifications.transferPopup.initiatedBy', {
          initiator: getDisplayUserLabel(notification.initiatedBy, t),
        }),
        t('notifications.transferPopup.expiresAt', {
          date: notification.expiresAt
            ? new Date(notification.expiresAt).toLocaleString()
            : t('common.notAvailable'),
        }),
      ];
    }

    return [
      t('notifications.transferPopup.organization', {
        organization: notification.organization?.name || t('common.notAvailable'),
      }),
      t('notifications.transferPopup.initiatedBy', {
        initiator: getDisplayUserLabel(notification.initiatedBy, t),
      }),
      t('notifications.transferPopup.expiresAt', {
        date: notification.expiresAt
          ? new Date(notification.expiresAt).toLocaleString()
          : t('common.notAvailable'),
      }),
    ];
  };

  const handleInvitationDismiss = () => {
    if (!activeNotification) {
      return;
    }
    const notificationId =
      activeNotification.notificationType === 'invite'
        ? activeNotification.inviteId
        : activeNotification.transferId;
    setDismissedNotificationIds(prev => [...prev, notificationId]);
  };

  const handleInvitationAccept = async () => {
    if (!activeNotification) {
      return;
    }
    try {
      const payload =
        activeNotification.notificationType === 'invite'
          ? {
              notificationType: activeNotification.notificationType,
              inviteType: activeNotification.inviteType,
              inviteId: activeNotification.inviteId,
            }
          : {
              notificationType: activeNotification.notificationType,
              transferId: activeNotification.transferId,
            };
      const response = await acceptNotification(payload).unwrap();
      dispatch(setCredentials({ token: response.token, user: response }));
      if (activeNotification.notificationType === 'invite') {
        void trackInvitationResponded({
          scope: activeNotification.inviteType === 'project' ? 'project' : 'organization',
          decision: 'accepted',
          source: 'notifications',
        });
      }
      const notificationId =
        activeNotification.notificationType === 'invite'
          ? activeNotification.inviteId
          : activeNotification.transferId;
      const transferSuccessKey =
        activeNotification.notificationType === 'project_transfer'
          ? 'notifications.projectTransferAcceptSuccess'
          : 'notifications.transferAcceptSuccess';
      message.success(
        activeNotification.notificationType === 'invite'
          ? t('invites.acceptSuccess')
          : t(transferSuccessKey)
      );
      setDismissedNotificationIds(prev => [...prev, notificationId]);
    } catch (err: any) {
      const transferErrorKey =
        activeNotification.notificationType === 'project_transfer'
          ? 'notifications.projectTransferAcceptError'
          : 'notifications.transferAcceptError';
      message.error(
        getMutationErrorMessage(
          err,
          activeNotification.notificationType === 'invite'
            ? t('invites.acceptError')
            : t(transferErrorKey)
        )
      );
    }
  };

  return (
    <>
      <Layout className={styles.layoutRoot}>
        <Sidebar
          collapsed={collapsed}
          isMobile={isMobile}
          mobileOpen={mobileMenuOpen}
          onMobileClose={handleMobileMenuClose}
        />
        <Layout className={styles.layoutMain}>
          <Header
            onMenuClick={handleMenuClick}
            collapsed={isMobile ? !mobileMenuOpen : collapsed}
            isMobile={isMobile}
          />
          {inactiveProShowHeaderBanner && inactiveProBillingHref ? (
            <div className={styles.billingAlertBannerSlot}>
              <AppInactiveProHeaderBanner
                variant={inactiveProNudgeVariant || 'inactive_pro'}
                organizationLabel={inactiveProOrganizationLabel}
                billingPath={inactiveProBillingHref}
                grantEndsAt={inactiveProGrantEndsAt}
                planName={inactivePaidPlanName}
                isMobile={isMobile}
              />
            </div>
          ) : null}
          <Content className={styles.contentViewport}>
            <div className={styles.contentInner}>
              <Outlet />
            </div>
          </Content>
          <Footer />
        </Layout>
      </Layout>
      <AppConfirmationModalBase
        open={Boolean(activeNotification)}
        title={
          activeNotification?.notificationType === 'invite'
            ? t('invites.confirm.title')
            : activeNotification?.notificationType === 'project_transfer'
              ? t('notifications.transferPopup.projectTitle')
              : t('notifications.transferPopup.title')
        }
        onCancel={handleInvitationDismiss}
        onConfirm={handleInvitationAccept}
        confirmText={
          activeNotification?.notificationType === 'invite'
            ? t('invites.confirm.accept')
            : t('notifications.transferAccept')
        }
        cancelText={t('invites.confirm.decline')}
        isLoading={isAcceptingNotification}
        descriptionLines={
          activeNotification ? buildNotificationDescriptionLines(activeNotification) : []
        }
      />
      <AppInactiveProResumeNudgeModal
        open={inactiveProNudgeOpen}
        variant={inactiveProNudgeVariant || 'inactive_pro'}
        organizationLabel={inactiveProOrganizationLabel}
        grantEndsAt={inactiveProGrantEndsAt}
        planName={inactivePaidPlanName}
        onDismiss={dismissInactiveProNudge}
        onResumePro={resumeInactivePro}
        isLoading={isInactiveProCheckoutLoading}
      />
    </>
  );
};

export default MainLayout;
